import {
  memo, useEffect, useMemo, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';
import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';
import Switcher from '../ui/Switcher';
import ProtocolEditorModal, { type CatalogCategory, type ProtocolItem } from './ProtocolEditorModal';

import styles from './ProtocolsView.module.scss';

const ProtocolsView = () => {
  const [protocols, setProtocols] = useState<ProtocolItem[]>([]);
  const [catalog, setCatalog] = useState<CatalogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'PAUSED'>('ALL');

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState<ProtocolItem | undefined>(undefined);

  const loadData = useLastCallback(async () => {
    setIsLoading(true);
    try {
      const [protoRes, catRes] = await Promise.all([
        fetch(`${BACKEND_URL}/protocols`),
        fetch(`${BACKEND_URL}/protocols/tools-catalog`),
      ]);

      if (protoRes.ok) {
        const protoData = await protoRes.json();
        setProtocols(protoData);
      }
      if (catRes.ok) {
        const catData = await catRes.json();
        setCatalog(catData.data || []);
      }
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeCount = useMemo(() => {
    return protocols.filter((p) => p.is_active).length;
  }, [protocols]);

  const filteredProtocols = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    return protocols.filter((p) => {
      if (statusFilter === 'ACTIVE' && !p.is_active) return false;
      if (statusFilter === 'PAUSED' && p.is_active) return false;

      if (!query) return true;

      const matchesTitle = p.title.toLowerCase().includes(query);
      const matchesDesc = (p.description || '').toLowerCase().includes(query);
      const matchesTriggers = (p.triggers || []).some((t) => t.toLowerCase().includes(query));

      return matchesTitle || matchesDesc || matchesTriggers;
    });
  }, [protocols, searchQuery, statusFilter]);

  const handleOpenCreateModal = useLastCallback(() => {
    setSelectedProtocol(undefined);
    setIsEditorOpen(true);
  });

  const handleOpenEditModal = useLastCallback((protocol: ProtocolItem) => {
    setSelectedProtocol(protocol);
    setIsEditorOpen(true);
  });

  const handleCloseModal = useLastCallback(() => {
    setIsEditorOpen(false);
    setSelectedProtocol(undefined);
  });

  const handleToggle = useLastCallback(async (id: number) => {
    setProtocols((prev) => prev.map((p) => (p.id === id ? { ...p, is_active: !p.is_active } : p)));
    try {
      await fetch(`${BACKEND_URL}/protocols/${id}/toggle`, { method: 'POST' });
    } catch {
      // Fallback
    }
  });

  const handleDelete = useLastCallback(async (protocol: ProtocolItem) => {
    if (!protocol.id) return;
    if (!window.confirm(`¿Estás seguro de eliminar el protocolo "${protocol.title}"?`)) {
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/protocols/${protocol.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setProtocols((prev) => prev.filter((p) => p.id !== protocol.id));
      }
    } catch {
      // Fallback
    }
  });

  const handleSaveProtocol = useLastCallback(async (payload: ProtocolItem) => {
    setIsSaving(true);
    try {
      if (payload.id) {
        const res = await fetch(`${BACKEND_URL}/protocols/${payload.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const updated = await res.json();
          setProtocols((prev) => prev.map((p) => (p.id === payload.id ? updated : p)));
          setIsEditorOpen(false);
        }
      } else {
        const res = await fetch(`${BACKEND_URL}/protocols`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const created = await res.json();
          setProtocols((prev) => [created, ...prev]);
          setIsEditorOpen(false);
        }
      }
    } catch {
      // Fallback
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <div className={styles.root}>
      {/* Top Header */}
      <div className={styles.headerBar}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Icon name="ai" />
          </div>
          <div>
            <div className={styles.headerTitleGroup}>
              <h2 className={styles.headerTitle}>
                Protocolos de Atención (Playbooks)
              </h2>
              <span className={styles.statusCounterBadge}>
                {activeCount}
                {' '}
                activos de
                {' '}
                {protocols.length}
              </span>
            </div>
            <p className={styles.headerSubtitle}>
              Reglas predeterminadas, directrices operativas y variables de contexto ejecutadas por el Asistente de IA.
            </p>
          </div>
        </div>

        <button
          type="button"
          className={styles.btnNewProtocol}
          onClick={handleOpenCreateModal}
        >
          <Icon name="add" />
          <span>Nuevo Protocolo</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className={styles.filterBar}>
        <input
          type="text"
          className={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por título, disparador o contenido..."
        />

        <div className={styles.filterPillGroup}>
          <button
            type="button"
            className={buildClassName(styles.filterPill, statusFilter === 'ALL' && styles.filterPillActive)}
            onClick={() => setStatusFilter('ALL')}
          >
            Todos (
            {protocols.length}
            )
          </button>
          <button
            type="button"
            className={buildClassName(styles.filterPill, statusFilter === 'ACTIVE' && styles.filterPillActive)}
            onClick={() => setStatusFilter('ACTIVE')}
          >
            Activos (
            {activeCount}
            )
          </button>
          <button
            type="button"
            className={buildClassName(styles.filterPill, statusFilter === 'PAUSED' && styles.filterPillActive)}
            onClick={() => setStatusFilter('PAUSED')}
          >
            Pausados (
            {protocols.length - activeCount}
            )
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div style="padding: 3rem; text-align: center; color: var(--color-text-secondary); font-size: 0.8125rem;">
          Cargando protocolos de atención...
        </div>
      ) : (
        /* Cards Grid */
        <div className={styles.grid}>
          {filteredProtocols.map((proto) => (
            <div key={proto.id} className={styles.card}>
              <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                <div className={styles.cardHeader}>
                  <h4 className={styles.cardTitle}>{proto.title}</h4>
                  {Boolean(proto.id) && (
                    <Switcher
                      id={`protocol-switch-${proto.id}`}
                      label={proto.title}
                      checked={proto.is_active}
                      onCheck={() => handleToggle(proto.id!)}
                    />
                  )}
                </div>

                <p className={styles.cardDescription}>
                  {proto.description || 'Sin descripción adicional.'}
                </p>
              </div>

              {/* Triggers */}
              <div className={styles.triggersSection}>
                <span className={styles.triggersLabel}>
                  Disparadores (
                  {proto.triggers?.length || 0}
                  ):
                </span>
                <div className={styles.triggersWrap}>
                  {(proto.triggers || []).slice(0, 4).map((trig) => (
                    <span key={trig} className={styles.chip}>
                      {trig}
                    </span>
                  ))}
                  {(proto.triggers || []).length > 4 && (
                    <span style="font-size: 0.6875rem; color: var(--color-text-secondary); font-weight: 600;">
                      +
                      {proto.triggers.length - 4}
                      {' '}
                      más
                    </span>
                  )}
                </div>
              </div>

              {/* Card Footer Actions */}
              <div className={styles.cardFooter}>
                <span className={styles.cardSlug}>
                  /
                  {proto.slug}
                </span>

                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.btnCardAction}
                    title="Editar protocolo"
                    onClick={() => handleOpenEditModal(proto)}
                  >
                    <Icon name="edit" />
                  </button>
                  <button
                    type="button"
                    className={buildClassName(styles.btnCardAction, styles.btnDeleteAction)}
                    title="Eliminar protocolo"
                    onClick={() => handleDelete(proto)}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredProtocols.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateTitle}>
                No se encontraron protocolos
              </p>
              <p className={styles.emptyStateSubtitle}>
                Intenta con otros términos de búsqueda o crea un nuevo protocolo de atención.
              </p>
              <button
                type="button"
                className={styles.btnNewProtocol}
                onClick={handleOpenCreateModal}
              >
                <Icon name="add" />
                <span>Crear Protocolo</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Editor Modal */}
      <ProtocolEditorModal
        isOpen={isEditorOpen}
        protocol={selectedProtocol}
        catalog={catalog}
        isSaving={isSaving}
        onClose={handleCloseModal}
        onSave={handleSaveProtocol}
      />
    </div>
  );
};

export default memo(ProtocolsView);
